import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ProfileDto {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  job_title: string | null;
  avatar_url: string | null;
  is_sso_user: boolean;
  mfa_enabled: boolean;
  timezone: string | null;
  role_name: string | null;
}

export interface MfaSetupDto {
  qr_code_data_url: string;
  manual_entry_code: string;
}

export interface OrganizationDto {
  id: string;
  name: string;
  slug: string;
  plan: string;
  user_count: number;
  theme_color: string | null;
  sidebar_text_color: 'white' | 'black' | null;
  sidebar_color: string | null;
}

@Injectable({ providedIn: 'root' })
export class SettingsApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/account`;

  getProfile(): Promise<ProfileDto> {
    return firstValueFrom(this.http.get<ProfileDto>(`${this.base}/me`));
  }

  updateProfile(req: { first_name: string; last_name: string; phone: string | null; timezone: string | null }): Promise<void> {
    return firstValueFrom(this.http.put<void>(`${this.base}/profile`, req));
  }

  changePassword(req: { current_password: string; new_password: string; confirm_password: string }): Promise<void> {
    return firstValueFrom(this.http.put<void>(`${this.base}/password`, req));
  }

  getOrganization(): Promise<OrganizationDto> {
    return firstValueFrom(this.http.get<OrganizationDto>(`${this.base}/organization`));
  }

  updateOrganization(req: {
    name: string;
    theme_color?: string | null;
    sidebar_text_color?: 'white' | 'black' | null;
    sidebar_color?: string | null;
  }): Promise<void> {
    return firstValueFrom(this.http.put<void>(`${this.base}/organization`, req));
  }

  setupMfa(): Promise<MfaSetupDto> {
    return firstValueFrom(this.http.post<MfaSetupDto>(`${this.base}/mfa/setup`, {}));
  }

  enableMfa(req: { totp_code: string }): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${this.base}/mfa/enable`, req));
  }

  disableMfa(req: { password: string }): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${this.base}/mfa/disable`, req));
  }

  uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const form = new FormData();
    form.append('file', file);
    return firstValueFrom(this.http.post<{ avatar_url: string }>(`${this.base}/avatar`, form));
  }

  deleteAvatar(): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.base}/avatar`));
  }
}
